import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnType, TablePaginationConfig } from 'antd/lib/table';

import { IngestionSourceModelOutput, IngestionSourceParametersType } from '../../../../../../gamma-api';
import IbIcon from '../../../../../components/common/IbIcon';
import { formatDateTimeShort, utcTimeToLocal } from '../../../../../utils/stringUtil';
import IbButton from '../../../../../components/common/IbButton';
import IbTable from '../../../../../components/common/IbTable';
import IbContextMenu from '../../../../../components/common/IbContextMenu';
import SourceStatus from '../SourceStatus';
import SourceVolume from '../SourceVolume';
import SourceTitle from '../SourceTitle';
import IbPagination from '../../../../../components/common/IbPagination';
import { SOURCES_PAGE_SIZE } from '../../../../../constants';

import './index.less';

import { MAIN_CLASS_NAME, PAGINATION_ROW_CLASS_NAME } from './const';

export interface ISourceTableProps {
  dataLoading: boolean;
  pageLoading: boolean;
  sources: IngestionSourceModelOutput[];
  pagination: TablePaginationConfig;
  searchText?: string;
  onOpen?: (source: IngestionSourceModelOutput) => void;
  onSetup?: (source: IngestionSourceModelOutput) => void;
  onIngest?: (source: IngestionSourceModelOutput) => void;
  onDelete?: (source: IngestionSourceModelOutput) => void;
  onRestart?: (source: IngestionSourceModelOutput) => void;
  onSchedule?: (source: IngestionSourceModelOutput) => void;
  onPaginationChange: (page: number) => void;
}

const SourceTable: React.FC<ISourceTableProps> = ({
  dataLoading,
  pageLoading,
  sources,
  pagination,
  searchText,
  onOpen,
  onSetup,
  onIngest,
  onDelete,
  onRestart,
  onSchedule,
  onPaginationChange,
}) => {
  const { t, i18n } = useTranslation();

  const [sourceId, setSourceId] = useState<string>();

  const onStatusClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
  };

  const onRowClick = (source: IngestionSourceModelOutput, column: ColumnType<unknown>) => {
    if (column.title) {
      onOpen?.(source);
    }
  };

  const onMenuClose = () => {
    setSourceId(undefined);
  };

  const renderName = (source: IngestionSourceModelOutput) => {
    const parameters = source.versions.latest.source.parameters;
    return <SourceTitle name={parameters.name} searchText={searchText} type={parameters.type} />;
  };

  const renderStatus = (source: IngestionSourceModelOutput) => {
    const rolling = dataLoading || !!source.versions.rolling;

    const onStatusRestart = () => {
      !rolling && onRestart?.(source);
    };

    return (
      <SourceStatus
        action={source.versions.latest.source.instruction.action}
        direction="vertical"
        loading={dataLoading}
        problem={source.problem}
        rolling={rolling}
        stage={source.stage}
        status={source.status}
        onClick={onStatusClick}
        onRestart={onStatusRestart}
      />
    );
  };

  const renderSchedule = (source: IngestionSourceModelOutput) => {
    const scheduleSettings = source.schedule?.settings;
    if (!scheduleSettings?.enabled) return t('Source table: Schedule: None');

    const { daily_time, weekly_times } = scheduleSettings;

    if (daily_time) {
      const time = utcTimeToLocal(daily_time, 'HH:mm');
      return t('Source table: Schedule: Every day', { time });
    }

    if (weekly_times && Object.keys(weekly_times).length > 0) {
      const sortedEntries = Object.entries(weekly_times).sort(([a], [b]) => Number(a) - Number(b));

      const groups: { days: number[]; time: string }[] = [];
      for (const [day, time] of sortedEntries) {
        const dayNum = Number(day);
        const localTime = utcTimeToLocal(time, 'HH:mm');
        const last = groups[groups.length - 1];
        if (last && last.time === localTime && last.days[last.days.length - 1] === dayNum - 1) {
          last.days.push(dayNum);
        } else {
          groups.push({ days: [dayNum], time: localTime });
        }
      }

      const parts = groups.map(({ days, time }) => {
        const first = t(`Source table: Schedule: Day ${days[0]}`);
        const dayLabel =
          days.length === 1 ? first : `${first}-${t(`Source table: Schedule: Day ${days[days.length - 1]}`)}`;
        return t('Source table: Schedule: With time', { days: dayLabel, time });
      });
      return parts.join(', ');
    }
  };

  const renderVolume = (source: IngestionSourceModelOutput) => {
    return <SourceVolume type={source.versions.latest.source.parameters.type} value={source.volumes.latest} />;
  };

  const renderDate = (source: IngestionSourceModelOutput) => {
    const dateTime = formatDateTimeShort(source.ingested_on, i18n.language);
    if (!source.ingestion_run_type) return dateTime;
    const runType = t(`Source table: Schedule: ${source.ingestion_run_type}`);
    return `${dateTime} (${runType})`;
  };

  const renderContext = (source: IngestionSourceModelOutput) => {
    const rolling = dataLoading || !!source.versions.rolling;

    const onButtonClick = () => {
      setSourceId(source.source_id);
    };

    const onOpenSelect = () => {
      onOpen?.(source);
    };

    const onSetupSelect = () => {
      onSetup?.(source);
    };

    const onScheduleSelect = () => {
      onSchedule?.(source);
    };

    const onRefreshSelect = () => {
      !rolling && onIngest?.(source);
    };

    const onDeleteSelect = () => {
      !rolling && onDelete?.(source);
    };

    const menuItems = [
      {
        icon: <IbIcon iconName="view-list" size={20} />,
        text: t('Source table: Context: Open'),
        onSelect: onOpenSelect,
      },
      {
        icon: <IbIcon iconName="setting-two" size={20} />,
        text: t('Source table: Context: Setup'),
        onSelect: onSetupSelect,
      },
      ...(source.versions.latest.source.parameters.type !== IngestionSourceParametersType.File
        ? [
            {
              icon: <IbIcon iconName="calendar-three" size={20} />,
              text: t('Source table: Context: Schedule'),
              onSelect: onScheduleSelect,
            },
          ]
        : []),
      {
        icon: <IbIcon iconName="refresh" size={20} />,
        text: t('Source table: Context: Ingest'),
        onSelect: onRefreshSelect,
        disabled: rolling,
      },
      {
        icon: <IbIcon iconName="delete" size={20} />,
        text: t('Source table: Context: Delete'),
        onSelect: onDeleteSelect,
        disabled: rolling,
      },
    ];

    return (
      <IbContextMenu menuItems={menuItems} visible={source.source_id == sourceId} onClose={onMenuClose}>
        <IbButton icon={<IbIcon iconName="more-one" />} type="icon" onClick={onButtonClick} />
      </IbContextMenu>
    );
  };

  const columns = [
    {
      title: t('Source table: Name'),
      render: renderName,
      width: '20%',
      ellipsis: true,
    },
    {
      title: t('Source table: Volume'),
      render: renderVolume,
      width: '20%',
    },
    {
      title: t('Source table: Status'),
      render: renderStatus,
      width: '20%',
    },
    {
      title: t('Source table: Schedule'),
      render: renderSchedule,
      width: '20%',
    },
    {
      title: t('Source table: Date'),
      render: renderDate,
      width: '20%',
    },
    {
      title: '',
      render: renderContext,
      width: 40,
    },
  ];

  return (
    <>
      <IbTable
        className={MAIN_CLASS_NAME}
        columns={columns}
        dataSource={sources}
        loading={dataLoading || pageLoading}
        rowKey="source_id"
        onRowClick={onRowClick}
      />
      {!dataLoading && (pagination.total ?? 0) > SOURCES_PAGE_SIZE && (
        <div className={PAGINATION_ROW_CLASS_NAME}>
          <IbPagination
            current={pagination.current}
            defaultCurrent={1}
            defaultPageSize={SOURCES_PAGE_SIZE}
            pageSize={SOURCES_PAGE_SIZE}
            showSizeChanger={false}
            total={pagination.total}
            onChange={onPaginationChange}
          />
        </div>
      )}
    </>
  );
};

export default SourceTable;
